import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Printer, Calendar, Clock, User, Wrench, Trash2, CheckCircle, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { parseApiError } from '../lib/parseApiError';
import { formatDate } from '../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI_ATTIVITA = [
  { value: 'installazione', label: 'Installazione' },
  { value: 'verifica_periodica', label: 'Verifica Periodica' },
  { value: 'manutenzione', label: 'Manutenzione' },
  { value: 'riparazione', label: 'Riparazione' },
  { value: 'sostituzione', label: 'Sostituzione' }
];

export default function ImpiantoDetailPage() {
  const { id } = useParams();
  const { isAdmin, user } = useAuth();
  const [impianto, setImpianto] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [puntoVendita, setPuntoVendita] = useState(null);
  const [attivita, setAttivita] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tecnicoSearch, setTecnicoSearch] = useState('');
  const [formData, setFormData] = useState({
    tipo_attivita: 'verifica_periodica',
    data_attivita: new Date().toISOString().split('T')[0],
    ora_inizio: '',
    ora_fine: '',
    durata_ore: '',
    descrizione: '',
    note: '',
    tecnico_id: ''
  });

  const fetchTecnici = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tecnici`);
      setTecnici(res.data);
    } catch (error) {
      console.error('Errore caricamento tecnici:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [impiantoRes, attivitaRes] = await Promise.all([
        axios.get(`${API}/impianti/${id}`),
        axios.get(`${API}/attivita-impianto?impianto_id=${id}`)
      ]);
      setImpianto(impiantoRes.data);
      setAttivita(attivitaRes.data);
      
      // Fetch cliente
      if (impiantoRes.data.cliente_id) {
        const clienteRes = await axios.get(`${API}/clienti/${impiantoRes.data.cliente_id}`);
        setCliente(clienteRes.data);
      }
      
      // Fetch punto vendita
      if (impiantoRes.data.punto_vendita_id) {
        const puntoRes = await axios.get(`${API}/punti-vendita/${impiantoRes.data.punto_vendita_id}`);
        setPuntoVendita(puntoRes.data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    fetchTecnici();
  }, [fetchData, fetchTecnici]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tecnico_id) {
      toast.error('Seleziona un tecnico');
      return;
    }
    try {
      await axios.post(`${API}/attivita-impianto`, {
        impianto_id: id,
        tipo_attivita: formData.tipo_attivita,
        data_attivita: formData.data_attivita,
        ora_inizio: formData.ora_inizio || null,
        ora_fine: formData.ora_fine || null,
        durata_ore: formData.durata_ore ? parseFloat(formData.durata_ore) : null,
        descrizione: formData.descrizione,
        note: formData.note || null,
        tecnico_id: formData.tecnico_id
      });
      toast.success('Attività registrata');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const handleDelete = async (attivitaId) => {
    if (!window.confirm('Eliminare questa attività?')) return;
    try {
      await axios.delete(`${API}/attivita-impianto/${attivitaId}`);
      toast.success('Attività eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_attivita: 'verifica_periodica',
      data_attivita: new Date().toISOString().split('T')[0],
      ora_inizio: '',
      ora_fine: '',
      durata_ore: '',
      descrizione: '',
      note: '',
      tecnico_id: ''
    });
    setTecnicoSearch('');
  };

  const getTipoAttivitaLabel = (tipo) => {
    const found = TIPI_ATTIVITA.find(t => t.value === tipo);
    return found ? found.label : tipo;
  };

  const getTipoAttivitaColor = (tipo) => {
    switch (tipo) {
      case 'installazione': return 'bg-success/20 text-success';
      case 'verifica_periodica': return 'bg-blue-600/20 text-blue-600';
      case 'manutenzione': return 'bg-amber-600/20 text-amber-600';
      case 'riparazione': return 'bg-red-600/20 text-red-600';
      case 'sostituzione': return 'bg-purple-600/20 text-purple-600';
      default: return 'bg-muted';
    }
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = (date - today) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!impianto) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Impianto non trovato</p>
        <Link to="/impianti">
          <Button variant="outline" className="mt-4 rounded-none">Torna agli Impianti</Button>
        </Link>
      </div>
    );
  }

  // Calcola statistiche
  const totaleOre = attivita.reduce((sum, a) => sum + (a.durata_ore || 0), 0);
  const ultimaVerifica = attivita.find(a => a.tipo_attivita === 'verifica_periodica');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="impianto-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/impianti">
            <Button variant="ghost" size="icon" className="rounded-none">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
              <Printer className="h-8 w-8" />
              {impianto.matricola}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {impianto.tipo} {impianto.marca && `- ${impianto.marca}`} {impianto.modello && impianto.modello}
            </p>
          </div>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-mono uppercase" data-testid="new-attivita-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuova Attività
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-mono uppercase tracking-wider">Registra Attività</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase">Tecnico *</Label>
                <div className="relative">
                  <Input
                    type="text"
                    value={tecnicoSearch}
                    onChange={(e) => {
                      setTecnicoSearch(e.target.value);
                      // Se cancella, resetta la selezione
                      if (!e.target.value) {
                        setFormData({...formData, tecnico_id: ''});
                      }
                    }}
                    className="rounded-none"
                    placeholder="Cerca tecnico per nome..."
                    data-testid="attivita-tecnico-search"
                  />
                  {tecnicoSearch && !formData.tecnico_id && (
                    <div className="absolute z-50 w-full mt-1 bg-background border border-border max-h-48 overflow-y-auto">
                      {tecnici
                        .filter(t => 
                          `${t.nome} ${t.cognome}`.toLowerCase().includes(tecnicoSearch.toLowerCase())
                        )
                        .map(t => (
                          <div
                            key={t.id}
                            className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                            onClick={() => {
                              setFormData({...formData, tecnico_id: t.id});
                              setTecnicoSearch(`${t.nome} ${t.cognome}`);
                            }}
                            data-testid={`tecnico-option-${t.id}`}
                          >
                            {t.nome} {t.cognome}
                            <span className="text-muted-foreground ml-2">({t.email})</span>
                          </div>
                        ))
                      }
                      {tecnici.filter(t => 
                        `${t.nome} ${t.cognome}`.toLowerCase().includes(tecnicoSearch.toLowerCase())
                      ).length === 0 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          Nessun tecnico trovato
                        </div>
                      )}
                    </div>
                  )}
                  {formData.tecnico_id && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setFormData({...formData, tecnico_id: ''});
                        setTecnicoSearch('');
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              
              <div>
                <Label className="text-xs font-mono uppercase">Tipo Attività *</Label>
                <Select value={formData.tipo_attivita} onValueChange={(v) => setFormData({...formData, tipo_attivita: v})}>
                  <SelectTrigger className="rounded-none" data-testid="attivita-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {TIPI_ATTIVITA.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Data *</Label>
                  <Input
                    type="date"
                    value={formData.data_attivita}
                    onChange={(e) => setFormData({...formData, data_attivita: e.target.value})}
                    className="rounded-none"
                    required
                    data-testid="attivita-data"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Ora Inizio</Label>
                  <Input
                    type="time"
                    value={formData.ora_inizio}
                    onChange={(e) => setFormData({...formData, ora_inizio: e.target.value})}
                    className="rounded-none"
                    data-testid="attivita-ora-inizio"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Ora Fine</Label>
                  <Input
                    type="time"
                    value={formData.ora_fine}
                    onChange={(e) => setFormData({...formData, ora_fine: e.target.value})}
                    className="rounded-none"
                    data-testid="attivita-ora-fine"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs font-mono uppercase">Durata (ore)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.durata_ore}
                  onChange={(e) => setFormData({...formData, durata_ore: e.target.value})}
                  className="rounded-none"
                  placeholder="Es: 1.5"
                  data-testid="attivita-durata"
                />
              </div>
              
              <div>
                <Label className="text-xs font-mono uppercase">Descrizione *</Label>
                <Textarea
                  value={formData.descrizione}
                  onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                  className="rounded-none"
                  required
                  placeholder="Descrizione dell'attività svolta..."
                  data-testid="attivita-descrizione"
                />
              </div>
              
              <div>
                <Label className="text-xs font-mono uppercase">Note</Label>
                <Textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  className="rounded-none"
                  placeholder="Note aggiuntive..."
                  data-testid="attivita-note"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                  Annulla
                </Button>
                <Button type="submit" className="rounded-none font-mono uppercase" data-testid="save-attivita-btn">
                  Salva
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Info Impianto */}
        <Card className="rounded-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Printer className="h-4 w-4" />
              Dettagli Impianto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tipo:</span>
              <span className="font-medium">{impianto.tipo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Marca:</span>
              <span className="font-medium">{impianto.marca || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Modello:</span>
              <span className="font-medium">{impianto.modello || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Installazione:</span>
              <span className="font-mono">{formatDate(impianto.data_installazione)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Scad. Verifica Periodica:</span>
              <div className="flex items-center gap-2">
                <span className="font-mono">{formatDate(impianto.data_scadenza_verifica)}</span>
                {impianto.data_scadenza_verifica && isExpired(impianto.data_scadenza_verifica) && (
                  <Badge variant="destructive" className="rounded-none text-xs">SCADUTO</Badge>
                )}
                {impianto.data_scadenza_verifica && isExpiringSoon(impianto.data_scadenza_verifica) && !isExpired(impianto.data_scadenza_verifica) && (
                  <Badge className="rounded-none text-xs bg-warning text-warning-foreground">IN SCADENZA</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Cliente */}
        <Card className="rounded-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <User className="h-4 w-4" />
              Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {cliente ? (
              <>
                <p className="font-bold text-lg">{cliente.ragione_sociale}</p>
                <p className="text-muted-foreground">{cliente.indirizzo_legale}</p>
                <p className="text-muted-foreground">{cliente.cap_legale} {cliente.citta_legale} {cliente.provincia_legale}</p>
                {cliente.telefono && <p className="font-mono">{cliente.telefono}</p>}
                {puntoVendita && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground">Punto Vendita:</p>
                    <p className="font-medium">{puntoVendita.nome}</p>
                    <p className="text-muted-foreground text-xs">{puntoVendita.indirizzo}, {puntoVendita.citta}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">Cliente non trovato</p>
            )}
          </CardContent>
        </Card>

        {/* Statistiche */}
        <Card className="rounded-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Statistiche
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground">Attività Totali</p>
              <p className="text-3xl font-black">{attivita.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Ore Totali Lavorate</p>
              <p className="text-3xl font-black">{totaleOre.toFixed(1)}h</p>
            </div>
            {ultimaVerifica && (
              <div>
                <p className="text-xs text-muted-foreground">Ultima Verifica</p>
                <p className="font-mono">{formatDate(ultimaVerifica.data_attivita)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Storico Attività */}
      <Card className="rounded-none">
        <CardHeader>
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Storico Attività ({attivita.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {attivita.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nessuna attività registrata. Clicca "Nuova Attività" per iniziare.
            </p>
          ) : (
            <div className="space-y-4">
              {attivita.map((att) => (
                <div key={att.id} className="border border-border p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-4 items-start">
                      <div className="text-center">
                        <Badge className={`rounded-none text-xs ${getTipoAttivitaColor(att.tipo_attivita)}`}>
                          {getTipoAttivitaLabel(att.tipo_attivita)}
                        </Badge>
                      </div>
                      <div>
                        <p className="font-bold">{att.descrizione}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(att.data_attivita)}
                          </span>
                          {(att.ora_inizio || att.ora_fine) && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {att.ora_inizio || '--:--'} - {att.ora_fine || '--:--'}
                            </span>
                          )}
                          {att.durata_ore && (
                            <span className="font-mono bg-primary/10 px-2 py-0.5 rounded">
                              {att.durata_ore}h
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {att.tecnico_nome}
                          </span>
                        </div>
                        {att.note && (
                          <p className="mt-2 text-sm text-muted-foreground italic">{att.note}</p>
                        )}
                      </div>
                    </div>
                    {isAdmin() && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 rounded-none text-destructive"
                        onClick={() => handleDelete(att.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
